import React, { ReactNode, useCallback, useMemo, useState } from 'react';

import ConfirmModal from './ConfirmModal';

type useConfirmReturn = [React.FC, () => Promise<boolean>, (event?: any) => void];

type CustomButton = {
  label: string;
  disabled?: boolean;
};

type useConfirmProps = {
  title: string;
  message?: string | ReactNode;
  confirmBtn?: CustomButton;
  cancelBtn?: CustomButton;
  modalId?: string;
  classes?: string;
  modalWidth?: string;
};

const useConfirm = ({
  title,
  message = 'Do you wish to proceed?',
  modalId = 'confirm-modal',
  confirmBtn = null,
  cancelBtn = null,
  classes = '',
  modalWidth = '520px',
}: useConfirmProps): useConfirmReturn => {
  const [promise, setPromise] = useState(null);
  const body = useMemo(
    () => ({
      title,
      message,
    }),
    [title, message]
  );

  const optionalProps = useMemo(
    () => ({
      ...(confirmBtn && { confirmBtn }),
      ...(cancelBtn && { cancelBtn }),
    }),
    [confirmBtn, cancelBtn]
  );

  const confirm = useCallback(
    (): Promise<boolean> =>
      new Promise((resolve) => {
        setPromise({ resolve });
      }),
    []
  );

  const handleClose = useCallback((event) => {
    event.stopPropagation();

    setPromise(null);
  }, []);

  const handleConfirm = useCallback(
    (event) => {
      event.stopPropagation();

      promise.resolve(true);
      handleClose(event);
    },
    [promise, handleClose]
  );

  const handleCancel = useCallback(
    (event) => {
      event.stopPropagation();

      promise.resolve(false);
      handleClose(event);
    },
    [promise, handleClose]
  );

  const ConfirmationModal = useCallback(
    () => (
      <ConfirmModal
        id={modalId}
        open={promise !== null}
        title={body.title}
        message={body.message}
        classes={classes}
        modalWidth={modalWidth}
        {...optionalProps}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    ),
    [
      promise,
      modalId,
      body.title,
      body.message,
      classes,
      handleClose,
      handleCancel,
      handleConfirm,
      modalWidth,
      optionalProps,
    ]
  );

  return [ConfirmationModal, confirm, handleClose];
};

export default useConfirm;
